import type { ImportantContactDtos } from '@lmc/ts-common'
import { Button, Descriptions, Heading, MoreInfoLink } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import MailIcon from '@lmc/ui-kit/src/general/Icon/customShapes/Mail'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import type { DescriptionsItemType } from 'antd/es/descriptions'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

type FamilyViewProps = {
  items?: DescriptionsItemType[]
  title: string
  icon?: ReactNode
  action?: () => void
  link?: string
  isEditMode?: boolean
  subtitle?: React.ReactNode
  className?: string
  hasAccess: boolean
  hasBeenInvited: boolean
  hasAcceptedInvitation: boolean
  onResendInvite: () => void
  onCreateInvite: () => void
  isSendingInvite: boolean
  residentName: string
  contact: Pick<ImportantContactDtos, 'email'>
  testId?: string
  disabledByPermission?: boolean
}

const FamilyView = ({
  items,
  title,
  className,
  hasAccess,
  hasBeenInvited,
  hasAcceptedInvitation,
  onResendInvite,
  onCreateInvite,
  isSendingInvite,
  residentName,
  contact,
  testId,
  disabledByPermission
}: FamilyViewProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const filteredItems = items?.filter((item) => Boolean(item))
  const hasEmail = Boolean(contact.email)

  const showCreateInviteButton =
    !hasBeenInvited && !hasAcceptedInvitation && hasEmail
  const showResendInviteButton = hasBeenInvited && !hasAcceptedInvitation
  const showContent = hasAccess && hasBeenInvited
  const showInvitationText = !hasBeenInvited && hasEmail
  const showNoEmailText = !hasBeenInvited && !hasEmail

  return (
    <MainWrapper
      className={className}
      data-testid={testId ? `${testId}-family-view` : 'family-view'}
    >
      <TopWrapper>
        <TitlesWrapper>
          <HeadingWrapper>
            <Heading
              level={3}
              data-testid={
                testId ? `${testId}-family-view-title` : 'family-view-title'
              }
            >
              {title}
            </Heading>
            <MoreInfoLink helpArticleUrl="" />
          </HeadingWrapper>
          {showInvitationText && (
            <TextWithMargins>
              {t(supConstants.INVITATION_TEXT, {
                name: residentName
              })}
            </TextWithMargins>
          )}
          {showNoEmailText && (
            <TextWithMargins>{t(supConstants.NO_EMAIL_TEXT)}</TextWithMargins>
          )}
        </TitlesWrapper>

        {showResendInviteButton && (
          <StyledButton
            isLoading={isSendingInvite}
            onClick={onResendInvite}
            variant="text"
            disabledByPermission={disabledByPermission}
            data-testid={
              testId
                ? `${testId}-family-view-resend-invite-btn`
                : 'family-view-resend-invite-btn'
            }
          >
            {MailIcon}
            {t(supConstants.RESEND_INVITE)}
          </StyledButton>
        )}
        {showCreateInviteButton && (
          <StyledButton
            isLoading={isSendingInvite}
            onClick={onCreateInvite}
            variant="text"
            disabledByPermission={disabledByPermission}
            data-testid={
              testId
                ? `${testId}-family-view-create-invite-btn`
                : 'family-view-create-invite-btn'
            }
          >
            {MailIcon}
            {t(supConstants.CREATE_INVITE)}
          </StyledButton>
        )}
      </TopWrapper>
      {showContent && (
        <>
          <DividerWithMargins />
          <Descriptions
            items={filteredItems}
            bordered
            column={1}
            data-testid={
              testId
                ? `${testId}-family-view-descriptions`
                : 'family-view-descriptions'
            }
          />
        </>
      )}
    </MainWrapper>
  )
}

const MainWrapper = styled.div``
const TopWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const TitlesWrapper = styled.div``
const StyledButton = styled(Button)`
  white-space: nowrap;
`
const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const DividerWithMargins = styled(Divider)`
  margin-top: 16px;
  margin-bottom: 16px;
`

const TextWithMargins = styled(Text)`
  display: block;
  margin-right: 24px;
`

export default FamilyView
